import React, { ChangeEvent, DragEvent, FC, useRef, useState } from 'react';
import { Button, Header, Icon, Message, Modal } from 'semantic-ui-react';
import {
    DocumentStorageUploadResponse,
    useCentralCalls,
} from '@src/rest/CentralCalls';
import SharepointLinkDialog from './SharepointLinkDialog';

interface DocumentDropZoneProps {
    clientId: string;
    documentItemId: string;
    className?: string;
    requireDocumentName?: boolean;
    onCustomUpload?: ( file: File, documentName: string ) => Promise<void>;
    onUploadSuccess?: ( response: DocumentStorageUploadResponse, file: File ) => void;
    onUploadError?: ( error: Error, file: File ) => void;
    /** Overrides how a SharePoint link is saved (e.g. to create the document item first). */
    onCustomSharepointLink?: ( sharepointLink: string, documentName: string ) => Promise<void>;
    onSharepointLinkSuccess?: () => void;
}

const formatFileSize = ( sizeInBytes: number ): string => {
    if( sizeInBytes < 1024 ) return `${ sizeInBytes } B`;

    const units = [ 'KB', 'MB', 'GB', 'TB' ];
    let value = sizeInBytes / 1024;
    let unitIndex = 0;

    while( value >= 1024 && unitIndex < units.length - 1 ){
        value /= 1024;
        unitIndex += 1;
    }

    return `${ value.toFixed( 2 ) } ${ units[ unitIndex ] }`;
};

const getDefaultDocumentName = ( fileName: string ): string => {
    const trimmed = fileName.trim();
    const lastDotIndex = trimmed.lastIndexOf( '.' );

    // Keep original name when there is no extension (or only a leading dot like ".env").
    if( lastDotIndex <= 0 || lastDotIndex === trimmed.length - 1 ) return trimmed;

    return trimmed.slice( 0, lastDotIndex );
};

const DocumentDropZone: FC<DocumentDropZoneProps> = ( {
    clientId,
    documentItemId,
    className,
    requireDocumentName,
    onCustomUpload,
    onUploadSuccess,
    onUploadError,
    onCustomSharepointLink,
    onSharepointLinkSuccess,
} ) => {

    const { uploadDocumentToStorage, addSharepointLinkVersion } = useCentralCalls();
    const fileInputRef = useRef<HTMLInputElement>( null );
    const isNewDocumentItemUpload = !!requireDocumentName && !documentItemId?.trim();
    const canAddSharepointLink = !!onCustomSharepointLink || !!documentItemId?.trim();

    const [ isSharepointOpen, setIsSharepointOpen ] = useState( false );

    const handleSubmitSharepointLink = async ( sharepointLink: string, documentName: string ) => {
        if( onCustomSharepointLink ){
            await onCustomSharepointLink( sharepointLink, documentName );
        } else {
            await addSharepointLinkVersion( clientId, documentItemId, sharepointLink );
        }
        onSharepointLinkSuccess?.();
    };

    const [ isDragOver, setIsDragOver ]       = useState( false ),
          [ pendingFile, setPendingFile ]     = useState<File | null>( null ),
          [ isConfirmOpen, setIsConfirmOpen ] = useState( false ),
          [ isUploading, setIsUploading ]     = useState( false ),
          [ errorMessage, setErrorMessage ]   = useState( '' ),
          [ documentName, setDocumentName ]   = useState( '' );

    const openConfirmForFile = ( file: File ) => {
        setPendingFile( file );
        setDocumentName( isNewDocumentItemUpload ? getDefaultDocumentName( file.name ) : '' );
        setErrorMessage( '' );
        setIsConfirmOpen( true );
    };

    const handleDragOver = ( event: DragEvent<HTMLDivElement> ) => {
        event.preventDefault();
        setIsDragOver( true );
    };

    const handleDragLeave = ( event: DragEvent<HTMLDivElement> ) => {
        event.preventDefault();
        setIsDragOver( false );
    };

    const handleDrop = ( event: DragEvent<HTMLDivElement> ) => {
        event.preventDefault();
        setIsDragOver( false );

        const file = event.dataTransfer.files?.[ 0 ];
        if( file ){
            openConfirmForFile( file );
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = ( event: ChangeEvent<HTMLInputElement> ) => {
        const file = event.target.files?.[ 0 ];
        if( file ){
            openConfirmForFile( file );
        }

        // Allow selecting the same file again after cancel/success.
        event.target.value = '';
    };

    const handleCancelConfirm = () => {
        if( isUploading ) return;
        setIsConfirmOpen( false );
        setPendingFile( null );
        setDocumentName( '' );
    };

    const handleConfirmUpload = async () => {
        if( !pendingFile ) return;

        if( !clientId?.trim() ){
            const err = new Error( 'clientId is required.' );
            setErrorMessage( err.message );
            onUploadError?.( err, pendingFile );
            return;
        }

        if( requireDocumentName && !documentName.trim() ){
            const err = new Error( 'Document Name is required.' );
            setErrorMessage( err.message );
            onUploadError?.( err, pendingFile );
            return;
        }

        if( !onCustomUpload && !documentItemId?.trim() ){
            const err = new Error( 'clientId and documentItemId are required.' );
            setErrorMessage( err.message );
            onUploadError?.( err, pendingFile );
            return;
        }

        setIsUploading( true );
        setErrorMessage( '' );

        try {
            if( onCustomUpload ){
                await onCustomUpload( pendingFile, documentName.trim() );
                onUploadSuccess?.( { success: true, documentId: '' }, pendingFile );
            } else {
                const response = await uploadDocumentToStorage( clientId, documentItemId, pendingFile );

                onUploadSuccess?.( response, pendingFile );
            }
            setIsConfirmOpen( false );
            setPendingFile( null );
            setDocumentName( '' );
        } catch( err ){ 
            const uploadError = err instanceof Error
                ? err
                : new Error( 'Upload failed.' );

            setErrorMessage( uploadError.message );
            onUploadError?.( uploadError, pendingFile );
        } finally {
            setIsUploading( false );
        }
    };

    const zoneClassName = [
        'document-drop-zone',
        isDragOver ? 'is-drag-over' : '',
        className ?? '',
    ].filter( Boolean ).join( ' ' );

    return (
        <>
            <div
                className={ zoneClassName }
                role={ 'button' }
                tabIndex={ 0 }
                onDragOver={ handleDragOver }
                onDragLeave={ handleDragLeave }
                onDrop={ handleDrop }
                onClick={ handleBrowseClick }
                onKeyDown={ e => {
                    if( e.key === 'Enter' || e.key === ' ' ){
                        e.preventDefault();
                        handleBrowseClick();
                    }
                } }
            >
                <Icon name={ 'upload' } size={ 'big' }/>
                <div className={ 'document-drop-zone-title' }>Drag &amp; drop to upload a document</div>
                <div className={ 'document-drop-zone-subtitle' }>
                    or click to choose a file
                </div>
                { canAddSharepointLink && (
                    <button
                        type={ 'button' }
                        className={ 'document-drop-zone-sharepoint-btn' }
                        onClick={ e => {
                            e.stopPropagation();
                            setIsSharepointOpen( true );
                        } }
                        onKeyDown={ e => e.stopPropagation() }
                    >
                        <Icon name={ 'linkify' } /> Add SharePoint link
                    </button>
                ) }
            </div>

            <SharepointLinkDialog
                open={ isSharepointOpen }
                requireDocumentName={ isNewDocumentItemUpload }
                onClose={ () => setIsSharepointOpen( false ) }
                onSubmit={ handleSubmitSharepointLink }
            />

            <input
                ref={ fileInputRef }
                type={ 'file' }
                style={ { display: 'none' } }
                onChange={ handleFileInputChange }
            />

            { errorMessage && (
                <Message
                    negative
                    icon={ 'warning circle' }
                    className={ 'document-drop-zone-message' }
                    content={ errorMessage }
                />
            ) }

            <Modal
                size={ 'small' }
                open={ isConfirmOpen }
                onClose={ handleCancelConfirm }
                closeOnDimmerClick={ !isUploading }
                closeOnEscape={ !isUploading }
            >
                <Header icon={ 'file outline' } content={ 'Confirm document upload' }/>
                <Modal.Content>
                    { pendingFile && (
                        <div className={ 'document-drop-zone-confirm-details' }>
                            <p><strong>File:</strong> { pendingFile.name }</p>
                            <p><strong>Type:</strong> { pendingFile.type || 'Unknown' }</p>
                            <p><strong>Size:</strong> { formatFileSize( pendingFile.size ) }</p>
                            <p><strong>ClientId:</strong> { clientId }</p>
                            { documentItemId && <p><strong>DocumentItemId:</strong> { documentItemId }</p> }
                            { requireDocumentName && (
                                <div style={ { marginTop: 12 } }>
                                    <label htmlFor={ 'document-name-input' } style={ { display: 'block', marginBottom: 6, fontWeight: 600 } }>
                                        Document Name
                                    </label>
                                    <input
                                        id={ 'document-name-input' }
                                        type={ 'text' }
                                        value={ documentName }
                                        onChange={ e => setDocumentName( e.target.value ) }
                                        placeholder={ 'Enter document name' }
                                        style={ {
                                            width: '100%',
                                            border: '1px solid rgba(0,0,0,0.2)',
                                            borderRadius: 6,
                                            padding: '8px 10px',
                                        } }
                                    />
                                </div>
                            ) }
                        </div>
                    ) }
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={ handleCancelConfirm }
                        disabled={ isUploading }
                        content={ 'Cancel' }
                    />
                    <Button
                        positive
                        icon={ 'upload' }
                        labelPosition={ 'right' }
                        content={ isUploading ? 'Uploading...' : 'Confirm and Upload' }
                        loading={ isUploading }
                        disabled={ isUploading || !pendingFile }
                        onClick={ handleConfirmUpload }
                    />
                </Modal.Actions>
            </Modal>
        </>
    );
};

export default DocumentDropZone;
